import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { notification, Skeleton } from 'antd';
import React, { useCallback } from 'react';
import { FormFieldDefinition, TaskMessage } from '../../graphql/schema';
import FormattedMessage from '../base/FormattedMessage/formattedMessage';
import FilePreview from '../base/upload/FilePreview';
import FilePreviewContainer from '../base/upload/FilePreviewContainer';
import fields from '../formBuilder/fields';
import MessageFooter from './MessageFooter';
import GeneratingImage from '../base/GeneratingImage';

export enum TaskMessageLoadingState {
  INITIALIZING = 'INITIALIZING',
  GENERATING_IMAGE = 'GENERATING_IMAGE',
  SEARCHING_WEB = 'SEARCHING_WEB',
}

export type TaskMessageWithLoadingIndicator = TaskMessage & {
  loading?: TaskMessageLoadingState;
};

interface Props {
  message: TaskMessageWithLoadingIndicator;
  onEditMessage?: () => void;
  formFieldDefinitions: FormFieldDefinition[];
}

const Message = ({ message, onEditMessage, formFieldDefinitions }: Props) => {
  const [notificationApi, notificationContextHolder] =
    notification.useNotification();

  const formFields = message.input.formValues
    ? formFieldDefinitions.filter((formField) => {
        const field = fields[formField.type as any];

        return (
          field &&
          message.input.formValues[formField.name] !== undefined &&
          message.input.formValues[formField.name] !== null
        );
      })
    : [];

  const handleCopy = useCallback(
    (text?: string | null) => () => {
      if (!text) return;
      void navigator.clipboard.writeText(text);
      notificationApi.info({
        message: 'Die Nachricht wurde in die Zwischenablage kopiert',
      });
    },
    [notificationApi],
  );

  return (
    <div className="w-full flex flex-col space-y-6 px-2">
      {message.input.followUpPrompt && (
        <div className="w-full lg:w-3/4 self-end group">
          <div className="w-full bg-[#efefef] rounded-xl p-3 text-sm">
            {message.input.followUpPrompt?.label}
          </div>
        </div>
      )}

      {message.input.prompt && (
        <div className="w-full lg:w-3/4 self-end group">
          <div className="w-full bg-[#efefef] rounded-xl p-3 text-sm">
            {Object.values(message.input.prompt)
              .filter((v) => typeof v === 'string')
              .join('\n')}

            <FilePreviewContainer>
              {Object.values(message.input.prompt)
                .filter(
                  (v: any) =>
                    Array.isArray(v) || (typeof v === 'object' && v.id),
                )
                .map((file: any) => {
                  if (Array.isArray(file))
                    return (
                      <>
                        {file.map((v: any) => (
                          <FilePreview file={v} key={v.id} />
                        ))}
                      </>
                    );

                  return <FilePreview file={file} key={file.id} />;
                })}
            </FilePreviewContainer>

            <MessageFooter
              onEdit={onEditMessage}
              onCopy={handleCopy(message.input.prompt.prompt)}
            />
          </div>
        </div>
      )}

      {message.input.formValues && (
        <div className="w-full lg:w-3/4 self-end group">
          <div className="w-full bg-[#efefef] rounded-xl p-3 text-sm">
            {formFields.map((formField) => {
              const field = fields[formField.type];

              return (
                <div key={formField.name}>
                  <strong>{formField.label}: </strong>
                  {field.renderReadOnly(
                    message.input.formValues[formField.name],
                    formField,
                  )}
                </div>
              );
            })}
            {formFields.length === 0 && (
              <div className="italic">Keine Informationen zur Anfrage</div>
            )}

            <MessageFooter onEdit={onEditMessage} />
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row items-start group">
        <div className="w-full self-start  rounded-xl p-3 text-sm">
          {message.loading && !message.response?.text && (
            <>
              {message.loading === TaskMessageLoadingState.INITIALIZING && (
                <Skeleton
                  active
                  round
                  paragraph={{
                    rows: 2,
                  }}
                />
              )}

              {message.loading === TaskMessageLoadingState.GENERATING_IMAGE && (
                <GeneratingImage height={300} />
              )}

              {message.loading === TaskMessageLoadingState.SEARCHING_WEB && (
                <>
                  <Skeleton
                    active
                    round
                    paragraph={{
                      rows: 2,
                    }}
                  />
                  <div className="text-[#00000088] py-2">
                    Durchsuche das Internet...
                  </div>
                </>
              )}
            </>
          )}

          {message.loading && message.response?.text && (
            <div className="prose w-full max-w-full text-base">
              <FormattedMessage text={message.response.text} />{' '}
              <FontAwesomeIcon icon={faCircle} color="#ccc" />
            </div>
          )}

          {!message.loading && message.response?.text && (
            <div className="prose w-full max-w-full text-base">
              <FormattedMessage text={message.response.text} />
            </div>
          )}

          {!message.loading && message.response?.files && (
            <div className="flex flex-row space-x-2 w-full">
              {message.response.files
                .filter((file) => !!file)
                .map((file) => (
                  <FilePreview file={file} key={file.id} imageSize={300} />
                ))}
            </div>
          )}

          {!message.loading && (
            <MessageFooter
              onCopy={handleCopy(message.response?.text)}
              sources={message.response?.sources}
              ragDocumentReferences={
                message.response?.ragDocumentReferences ?? []
              }
            />
          )}
        </div>
      </div>

      {notificationContextHolder}
    </div>
  );
};

export default Message;
